import CloseIcon from "@mui/icons-material/Close";
import {
  isStaleCartError,
  clearStaleCartStorage,
  showStaleCartDialog,
  isNoCartError,
  showNoCartDialog,
} from "../../../../../utils/staleCartHandler";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Box, Checkbox, FormControlLabel, IconButton, Input, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import BarcodeReader from "react-barcode-reader";
import {
  AddItemThroughScanner,
  FilterProduct,
  createValidatedOrder,
  dataRetriveWhenPageRefresh,
  newCartItemsAddingProducts,
} from "../../../../../config/Services";
import StyledButton from "../../../product/all-products/components/custom/StyledButton";
import KeyboardDropDownModal from "../../../product/all-products/components/keyboardDropDownModal";
import { sortItems } from "../../../product/all-products/utils/cartUtils";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import SkeletonLoader from "../SkeletonLoader";
import BarcodeScannerComponent from "./BarcodeScannerComponent";
import CartItem from "./CartItem";
import "./ValidationModal.css";
import Spinner from "../../../../../components/Spinner";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  setBoardItem,
  setNewOds,
  setOdsCart,
} from "../../../product/all-products/redux/Actions/paginationActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  getAddNewOds,
  getOdsCart,
} from "../../../product/all-products/redux/Reducers/paginationReducer";
import useSubmit from "../../../product/hooks/useSubmit";
import { emit } from "react-native-react-bridge/lib/web";
import { getFromLocalStorage } from "../../../../../utils/localStorageHelpers";

const ValidationModal = ({
  orderId,
  orderDetails,
  closeModal,
  validationModal,
  moveItemToState,
  startPrint,
}) => {
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();
  const [isChecked, setIsChecked] = useState(false);
  const cartId = orderDetails?.baseOrderResponse?.cartId;
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const [data, setData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [totalRes, setTotalRes] = useState(null);
  const [searchActive, setSearchActive] = useState(false);
  const addNew = useSelector(getAddNewOds);
  const cartItems = useSelector(getOdsCart);

  const cartQuery = useQuery(["cart"], () => dataRetriveWhenPageRefresh(cartId), {
    retry: 2,
    refetchOnWindowFocus: false,
    onSuccess: (res) => {
      const newCartItems = sortItems(res?.data?.data?.data?.cartProducts);
      dispatch(setOdsCart(newCartItems));
    },

    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
      closeModal();
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: (payload) => createValidatedOrder(payload),
    onSuccess: (res) => {
      if (res?.data?.data?.es) {
        showSnackbar(res?.data?.data?.message, "error");
        return;
      }
      const retailType = localStorage.getItem("retailType");
      moveItemToState(
        orderId,
        "CREATED",
        "PACKAGED",
        retailType !== "RESTAURANT"
          ? () => {
              startPrint({ orderId: orderId, printType: "invoice", orderSource: "b2c" });
            }
          : undefined
      );
      showSnackbar("Order validated and moved to packed state", "success");
      dispatch(setBoardItem(null));
      closeModal();
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const invalidItems = useMemo(() => cartItems?.filter((item) => !item.isValidated), [cartItems]);
  const invalidItemExist = !!invalidItems?.length;
  const validatedItemCount = cartItems.length - invalidItems.length;

  const handleProceed = () => {
    // if (invalidItemExist) {
    //   showSnackbar("Please validate all items", "error");
    //   return;
    // }
    const sessionId = localStorage.getItem("sessionId");
    const licenseId = localStorage.getItem("licenseId");
    const { uidx, firstName, secondName } = getFromLocalStorage("retailUserDetails") || {};
    const payload = {
      sessionId: sessionId,
      licenseId: licenseId,
      orderId: orderId,
      cartId: cartId,
      inventoryCheck: "NO",
      paymentMethod: orderDetails?.orderBillingDetails?.paymentMethod,
      currentUser: `${firstName} ${secondName}`,
    };
    createOrderMutation.mutate(payload);
  };

  const clickPress = (value) => {
    setSearchActive(true);
    const suggestionPayload = {
      page: "1",
      pageSize: "100",
      query: value,
      storeLocations: [locId],
      storeIds: [orgId],
      flattenResponse: true,
    };
    setLoader(true);

    FilterProduct(suggestionPayload)
      .then((res) => {
        setLoader(false);
        setTotalRes(res?.data?.data?.data?.totalRecords);
        if (res?.data?.data?.data?.totalRecords === "0") {
          setData(null);
          setSearchActive(false);
        } else {
          setData(res?.data?.data?.data?.data);
        }
      })
      .catch((err) => {
        setLoader(false);
        showSnackbar(
          err?.message || err?.data?.response?.message || "Something went wrong ,try again later",
          "error"
        );
      });
  };

  const fetchInventory = useSubmit({
    service: newCartItemsAddingProducts,
    // condition: !!selectedGtin,
    onSuccess: (res) => {
      if (res?.data?.es === 0 || res?.data?.statusCode === 1101) {
        // length >= 1 covers single-batch too (not just multi-batch)
        if (res?.data?.data?.length >= 1) {
          const dataArr = res?.data?.data?.map((item) => {
            return {
              mrp: item?.mrp,
              sellingPrice: item?.sellingPrice,
              gtin: item?.gtin,
              batch: item?.batchNo,
              quantity: item?.availableUnits,
              sellingUnit: item?.sellingUnit,
              batchNum: item?.batchNo,
              offers: item.offersList ? item : null,
              purchasePrice: item?.purchasePrice,
              isMultiple: item?.multipleBatches,
              expiryDate: item?.expiryDate,
              brandName: item?.brand,
              packagingType: item?.packagingType,
              variantName: item?.itemName,
              vendorId: item?.vendorId,
            };
          });

          const lastItem = dataArr[dataArr.length - 1];
          addItemWithScanner(
            { gtin: lastItem?.gtin, cartId },
            lastItem?.mrp,
            lastItem?.sellingPrice,
            lastItem?.batchNo,
            lastItem?.purchasePrice,
            lastItem?.sellingUnit,
            lastItem?.offers,
            lastItem?.expiryDate?.split("+")[0],
            lastItem?.variantName,
            lastItem?.vendorId,
            lastItem?.packagingType,
            lastItem?.brandName
          );
        } else {
          const cartProducts = res?.data?.data.cartProducts;
          dispatch(setOdsCart(cartProducts));
          dispatch(setNewOds(false));
        }
      } else {
        const errMsg = res?.data?.message || "";
        if (isStaleCartError(errMsg)) {
          clearStaleCartStorage();
          showStaleCartDialog();
          return;
        }
        if (isNoCartError(errMsg)) {
          showNoCartDialog();
          return;
        }
        showSnackbar(errMsg, "error");
      }
    },
  });

  const addItemWithScanner = async (
    gtin,
    mrp,
    sp,
    batchNo,
    purchasePrice,
    sellingUnit,
    offer,
    expiryDate,
    itemName,
    vendorId,
    packagingType,
    brand
  ) => {
    const addCustomerPayload = {
      cartId: cartId,
      sellingPrice: sp,
      gtin: gtin?.gtin,
      locationId: locId,
      mrp: mrp,
      batchNo: batchNo,
      purchasePrice: purchasePrice,
      inventoryChecks: "NO",
      sellingUnit: sellingUnit || null,
      isOfferAvailable: offer?.offersList.length > 0 ? true : false,
      expiryDate: expiryDate?.split("+")[0],
      variantName: itemName,
      vendorId: vendorId,
      packagingType: packagingType,
      brandName: brand,
    };

    try {
      await AddItemThroughScanner(addCustomerPayload, sp || selectedMrp).then((res) => {
        if (res?.data?.data?.es === 1 || res?.data?.data?.es === 2) {
          const errMsg = res?.data?.data?.message || "";
          if (isStaleCartError(errMsg)) {
            clearStaleCartStorage();
            showStaleCartDialog();
            return;
          }
          if (isNoCartError(errMsg)) {
            showNoCartDialog();
            return;
          }
          showSnackbar(errMsg, "error");
          return;
        } else {
          const cartProducts = res?.data?.data?.data?.cartProducts;
          dispatch(setOdsCart(cartProducts));
          dispatch(setNewOds(false));
        }
      });
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Unable To Add";
      if (isStaleCartError(errMsg)) {
        clearStaleCartStorage();
        showStaleCartDialog();
        return;
      }
      if (isNoCartError(errMsg)) {
        showNoCartDialog();
        return;
      }
      showSnackbar(errMsg, "error");
      //   (isMobilePos || deviceType === "android") &&
      //     emit({
      //       type: "productAdded",
      //       data: {
      //         success: false,
      //       },
      //     });
    }
  };

  const debouncedClickPress = debounce(clickPress, 500);
  return (
    <>
      <div className="ods-modal-close-button">
        <IconButton
          aria-label="close"
          size={addNew ? "large" : "small"}
          onClick={addNew ? () => dispatch(setNewOds(false)) : closeModal}
          sx={{ float: addNew ? "left" : "right" }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
        {addNew && (
          <Typography variant="h5" className="validate-header-title">
            Add product
          </Typography>
        )}
      </div>
      {addNew ? (
        <div className="add-new-product-toggle">
          <BarcodeReader
            onScan={(sc) => {
              fetchInventory.submit({
                gtin: sc,
                cartId: cartId,
                locationId: locId,
                orgId: orgId,
              });
            }}
            onError={(err, msg) => {}}
            avgTimeByChar={120}
          />

          <Box className="add-or-scan-ods">
            <Input
              type="text"
              placeholder="Search Products here..."
              sx={{ width: "100%", marginTop: "1rem" }}
              className="search-add-item-input"
              onChange={(e) => debouncedClickPress(e.target.value)}
              InputProps={{ disableUnderline: true }}
            />
            {!searchActive && (
              <>
                <div>OR</div>
                <div>Scan to add a product</div>
              </>
            )}

            <Box mt={4}>{totalRes === "0" && <div>No result found...</div>}</Box>
            {data && (
              <Box className="scan-to-product-box">
                {loader ? (
                  <Spinner />
                ) : (
                  <>
                    <KeyboardDropDownModal
                      cartId={cartId}
                      // closeSearchProductModal={props.closeSearchProductModal}
                      data={data}
                      listType={"search-ods"}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        </div>
      ) : (
        <div>
          <BarcodeScannerComponent cartId={cartId} cartItems={cartItems} />
          <div className="validate-header-box">
            <div className="validate-header-title-box">
              <div className="validate-header-icon">
                <TaskAltIcon fontSize="small" />
              </div>
              <div>
                <Typography variant="h5" className="validate-header-title">
                  {`Validate Items (${validatedItemCount}/${cartItems?.length})`}
                </Typography>
                <div className="validate-header-subtitle">Scan the barcode to validate items</div>
              </div>
            </div>
            <StyledButton
              aria-label="close"
              size="small"
              onClick={() => dispatch(setNewOds(true))}
              className="add-new-btn-ods"
            >
              {/* <PostAddIcon fontSize="medium" /> */}+ Add item
            </StyledButton>
          </div>
          {cartQuery?.isLoading || cartQuery?.isFetching ? (
            <SkeletonLoader type="order-details" />
          ) : (
            <>
              <div className="validate-content-box">
                {cartItems?.map((item) => (
                  <CartItem item={item} cartId={cartId} key={item?.cartProductId} />
                ))}
              </div>
              {/* <FormControlLabel
                control={
                  <Checkbox checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} />
                }
                label="Proceed without validation"
              /> */}
              <div className="validate-proceed-btn">
                <StyledButton
                  //   disabled={!isChecked && invalidItemExist}
                  disabled={invalidItemExist}
                  onClick={handleProceed}
                  loading={createOrderMutation?.isLoading}
                >
                  proceed
                </StyledButton>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ValidationModal;
